'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import ProductCard from './ProductCard';
import FilterSidebar from './FilterSidebar';
import SearchBar from './SearchBar';
import ActiveFilters from './ActiveFilters';
import SortDropdown from './SortDropdown';
import InquiryModal from './InquiryModal';
import { useTranslation } from '@/hooks/useTranslation';
import { createClient } from '@/lib/supabase/client';

interface Product {
  id: string;
  name: string;
  manufacturer_id: string;
  category: string;
  subcategory: string;
  price: number | null;
  min_order_quantity: number | null;
  unit: string | null;
  images: string[] | null;
  specifications: any;
  is_active: boolean;
  manufacturers: {
    company_name: string;
    is_audited: boolean;
  } | null;
}

const ProductCatalogInteractive = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [sortBy, setSortBy] = useState('relevance');
  const [savedProducts, setSavedProducts] = useState<string[]>([]);
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState({
    categories: [] as string[],
    manufacturers: [] as string[],
    certifications: [] as string[],
    priceRanges: [] as string[],
    availability: [] as string[]
  });
  const [inquiryModal, setInquiryModal] = useState({
    isOpen: false,
    productId: '',
    productName: '',
    manufacturerId: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    const supabase = createClient();
    const { data, error } = await supabase
      .from('products')
      .select(`
        *,
        manufacturers!manufacturer_id (
          company_name,
          is_audited
        )
      `)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching products:', error);
    } else if (data) {
      console.log('Fetched products with manufacturers:', data);
      setProducts(data);
    }
    setLoading(false);
  };

  const searchSuggestions = [
    'Cotton Fabric',
    'Textile Machinery',
    'Industrial Equipment',
    'Packaging Materials',
    'Steel Products',
    'Electronic Components'
  ];

  // Calculate dynamic filter options based on actual product data
  const getManufacturersOptions = () => {
    const manufacturerMap = new Map<string, { name: string; count: number }>();

    products.forEach(product => {
      const manufacturerId = product.manufacturer_id;
      const manufacturerName = product.manufacturers?.company_name || 'Unknown';

      if (manufacturerMap.has(manufacturerId)) {
        manufacturerMap.get(manufacturerId)!.count++;
      } else {
        manufacturerMap.set(manufacturerId, { name: manufacturerName, count: 1 });
      }
    });

    return Array.from(manufacturerMap.entries())
      .map(([id, data]) => ({
        id,
        label: data.name,
        count: data.count
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  };

  const getCategoryOptions = () => {
    const categoryMap = new Map<string, number>();

    products.forEach(product => {
      const category = product.category || 'uncategorized';
      categoryMap.set(category, (categoryMap.get(category) || 0) + 1);
    });

    const predefinedCategories = [
      { id: 'Yarn & Spinning', label: 'Yarn & Spinning' },
      { id: 'Weaving & Knitting', label: 'Weaving & Knitting' },
      { id: 'Dyeing & Finishing', label: 'Dyeing & Finishing' },
      { id: 'Garments & Apparel Manufacturing', label: 'Garments & Apparel Manufacturing' },
      { id: 'Home Textiles', label: 'Home Textiles' },
      { id: 'Technical & Industrial Textiles', label: 'Technical & Industrial Textiles' },
      { id: 'Accessories & Trims', label: 'Accessories & Trims' },
      { id: 'Packaging & Textile Printing', label: 'Packaging & Textile Printing' }
    ];

    return predefinedCategories.map(cat => ({
      ...cat,
      count: categoryMap.get(cat.id) || 0
    }));
  };

  const getPriceRangeOptions = () => {
    const ranges = [
      { id: 'under-500', label: 'Under EGP 500', min: 0, max: 500 },
      { id: '500-2500', label: 'EGP 500 - 2,500', min: 500, max: 2500 },
      { id: '2500-5000', label: 'EGP 2,500 - 5,000', min: 2500, max: 5000 },
      { id: 'over-5000', label: 'Over EGP 5,000', min: 5000, max: Infinity }
    ];

    return ranges.map(range => ({
      id: range.id,
      label: range.label,
      count: products.filter(p => {
        if (!p.price) return false;
        const priceInEGP = p.price * 50;
        return priceInEGP >= range.min && priceInEGP < range.max;
      }).length
    }));
  };

  const filterOptions = {
    categories: getCategoryOptions(),
    manufacturers: getManufacturersOptions(),
    certifications: [
      { id: 'iso9001', label: 'ISO 9001', count: 0 },
      { id: 'ce', label: 'CE', count: 0 },
      { id: 'osha', label: 'OSHA', count: 0 }
    ],
    priceRanges: getPriceRangeOptions(),
    availabilityOptions: [
      { id: 'in-stock', label: 'In Stock', count: products.length }
    ]
  };

  const handleFilterChange = (filterType: string, value: string) => {
    setSelectedFilters((prev) => {
      const currentValues = prev[filterType as keyof typeof prev] as string[];
      const newValues = currentValues.includes(value)
        ? currentValues.filter((v) => v !== value)
        : [...currentValues, value];
      return { ...prev, [filterType]: newValues };
    });
  };

  const handleClearAllFilters = () => {
    setSelectedFilters({
      categories: [],
      manufacturers: [],
      certifications: [],
      priceRanges: [],
      availability: []
    });
  };

  const getActiveFilters = () => {
    const filters: { type: string; value: string; label: string }[] = [];
    Object.entries(selectedFilters).forEach(([type, values]) => {
      values.forEach((value) => {
        const option = filterOptions[type as keyof typeof filterOptions]?.find(
          (opt: any) => opt.id === value
        );
        if (option) {
          filters.push({ type, value, label: option.label });
        }
      });
    });
    return filters;
  };

  const handleSaveProduct = (productId: string) => {
    setSavedProducts((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
  };

  const handleInquiry = (productId: string) => {
    const product = products.find((p) => p.id === productId);
    if (product) {
      setInquiryModal({
        isOpen: true,
        productId,
        productName: product.name,
        manufacturerId: product.manufacturer_id
      });
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
  };

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (product.category && product.category.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesCategory = selectedFilters.categories.length === 0 ||
      selectedFilters.categories.includes(product.category);

    const matchesManufacturer = selectedFilters.manufacturers.length === 0 ||
      selectedFilters.manufacturers.includes(product.manufacturer_id);

    const matchesPriceRange = selectedFilters.priceRanges.length === 0 ||
      selectedFilters.priceRanges.some(rangeId => {
        if (!product.price) return false;
        const priceInEGP = product.price * 50;

        switch (rangeId) {
          case 'under-500':
            return priceInEGP < 500;
          case '500-2500':
            return priceInEGP >= 500 && priceInEGP < 2500;
          case '2500-5000':
            return priceInEGP >= 2500 && priceInEGP < 5000;
          case 'over-5000':
            return priceInEGP >= 5000;
          default:
            return false;
        }
      });

    return matchesSearch && matchesCategory && matchesManufacturer && matchesPriceRange;
  });

  // Convert to ProductCard format
  const displayProducts = filteredProducts.map(product => ({
    id: product.id,
    name: product.name,
    manufacturer: product.manufacturers?.company_name || 'Unknown',
    category: product.category || 'Uncategorized',
    image: product.images?.[0] || 'https://via.placeholder.com/400',
    alt: product.name,
    price: product.price ? `EGP ${(product.price * 50).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/${product.unit || 'unit'}` : 'Contact for price',
    moq: product.min_order_quantity ? `${product.min_order_quantity} ${product.unit || 'units'}` : 'Contact for MOQ',
    availability: 'In Stock' as const,
    certifications: [],
    rating: 4.5,
    verified: product.manufacturers?.is_audited || false
  }));

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-16" />
        <div className="max-w-[1400px] mx-auto px-6 py-8">
          <div className="h-12 bg-muted rounded-lg animate-pulse mb-6" />
          <div className="flex gap-6">
            <div className="hidden lg:block w-72 flex-shrink-0">
              <div className="h-96 bg-muted rounded-lg animate-pulse" />
            </div>
            <div className="flex-1">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) =>
                  <div key={i} className="h-96 bg-muted rounded-lg animate-pulse" />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="h-16" />

      <div className="bg-card border-b border-border py-8">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-3xl font-headline font-bold text-foreground mb-2">
                {t('productCatalogTitle')}
              </h1>
              <p className="text-muted-foreground">
                {t('browseProductsDesc')}
              </p>
            </div>
            <button
              onClick={() => setIsMobileFilterOpen(true)}
              className="lg:hidden flex items-center space-x-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg"
            >
              <Icon name="AdjustmentsHorizontalIcon" size={20} variant="outline" />
              <span className="font-semibold">{t('filters')}</span>
            </button>
          </div>

          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={(value) => {
              setSearchQuery(value);
              setShowSuggestions(value.length > 0);
            }}
            onSearch={() => setShowSuggestions(false)}
            suggestions={searchSuggestions.filter((s) =>
              s.toLowerCase().includes(searchQuery.toLowerCase())
            )}
            showSuggestions={showSuggestions}
            onSuggestionClick={handleSuggestionClick}
          />
        </div>
      </div>

      {/* Manufacturer Registration CTA */}
      <div className="bg-gradient-to-r from-conversion/10 via-conversion/5 to-conversion/10 border-y border-conversion/20">
        <div className="max-w-[1400px] mx-auto px-6 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-conversion/20 rounded-full flex items-center justify-center">
                <Icon name="BuildingOfficeIcon" size={24} variant="outline" className="text-conversion" />
              </div>
              <div>
                <h3 className="text-lg font-headline font-bold text-foreground mb-1">
                  Are you a manufacturer?
                </h3>
                <p className="text-sm text-muted-foreground">
                  List your products and factory for free to reach global buyers
                </p>
              </div>
            </div>
            <a
              href="/manufacturer-onboarding"
              className="flex items-center gap-2 px-6 py-3 bg-conversion text-conversion-foreground rounded-lg font-semibold hover:bg-conversion/90 transition-all duration-200 shadow-md hover:shadow-lg whitespace-nowrap"
            >
              <Icon name="PlusCircleIcon" size={20} variant="outline" />
              <span>Register Your Factory</span>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 py-8">
        <div className="flex gap-6">
          <FilterSidebar
            categories={filterOptions.categories}
            manufacturers={filterOptions.manufacturers}
            certifications={filterOptions.certifications}
            priceRanges={filterOptions.priceRanges}
            availabilityOptions={filterOptions.availabilityOptions}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onClearAll={handleClearAllFilters}
            isMobileOpen={isMobileFilterOpen}
            onMobileClose={() => setIsMobileFilterOpen(false)}
          />

          <div className="flex-1 min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
              <div className="flex-1">
                <ActiveFilters
                  filters={getActiveFilters()}
                  onRemove={handleFilterChange}
                  onClearAll={handleClearAllFilters}
                />
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {displayProducts.length} {t('productCatalogCount')}
                </span>
                <SortDropdown sortBy={sortBy} onSortChange={setSortBy} />
              </div>
            </div>

            {displayProducts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {displayProducts.map((product) =>
                  <ProductCard
                    key={product.id}
                    product={product}
                    onInquiry={handleInquiry}
                    onSave={handleSaveProduct}
                    isSaved={savedProducts.includes(product.id)}
                  />
                )}
              </div>
            ) : (
              <div className="text-center py-16">
                <Icon name="CubeIcon" size={48} variant="outline" className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No products found</h3>
                <p className="text-muted-foreground">Try adjusting your search or filters</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <InquiryModal
        isOpen={inquiryModal.isOpen}
        onClose={() => setInquiryModal({ isOpen: false, productId: '', productName: '', manufacturerId: '' })}
        productName={inquiryModal.productName}
        productId={inquiryModal.productId}
        manufacturerId={inquiryModal.manufacturerId}
      />
    </div>
  );
};

export default ProductCatalogInteractive;